import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import OnboardingAdminPanel from '../components/admin/OnboardingAdminPanel';
import { 
  Users, 
  CheckCircle, 
  Clock,
  DollarSign,
  Calendar,
  Shield,
  RefreshCw,
  Eye,
  Brain,
  XCircle,
  Activity,
  Zap,
  MessageCircleQuestion,
  ThumbsUp,
  ThumbsDown,
  Mail,
  GraduationCap
} from 'lucide-react';

const AdminPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [adminStats, setAdminStats] = useState({
    total_users: 0,
    total_clinics: 0,
    subscription_owners: 0,
    invited_members: 0,
    active_subscriptions: 0,
    trial_users: 0,
    suspended_users: 0,
    estimated_monthly_revenue: 0,
    conversion_rate: 0
  });
  
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCourtesyModal, setShowCourtesyModal] = useState(false);
  const [courtesyUserId, setCourtesyUserId] = useState(null);
  const [courtesyPeriod, setCourtesyPeriod] = useState('30d');
  const [includeWhatsappCourtesy, setIncludeWhatsappCourtesy] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAccountType, setFilterAccountType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [aiStatus, setAiStatus] = useState(null);
  const [aiStatusLoading, setAiStatusLoading] = useState(false);

  const [aiTelemetry, setAiTelemetry] = useState({ priority: [], last_used: null, attempts: [] });
  const [aiTelemetryLoading, setAiTelemetryLoading] = useState(false);

  const [supportFeedback, setSupportFeedback] = useState({
    summary: {
      total_feedback: 0,
      helpful_count: 0,
      unhelpful_count: 0,
      helpful_rate: 0
    },
    top_routes: [],
    items: []
  });
  const [supportFeedbackLoading, setSupportFeedbackLoading] = useState(false);

  const [supportArticlePerformance, setSupportArticlePerformance] = useState([]);
  const [supportArticlePerformanceLoading, setSupportArticlePerformanceLoading] = useState(false);

  const [knowledgeArticles, setKnowledgeArticles] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [selectedKnowledgeArticle, setSelectedKnowledgeArticle] = useState(null);
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);
  const [knowledgeSaving, setKnowledgeSaving] = useState(false);

  const [knowledgeSuggesting, setKnowledgeSuggesting] = useState(false);

  const [knowledgeHistory, setKnowledgeHistory] = useState([]);
  const [showKnowledgeHistoryModal, setShowKnowledgeHistoryModal] = useState(false);
  const [knowledgeHistoryLoading, setKnowledgeHistoryLoading] = useState(false);

  const [knowledgeSearch, setKnowledgeSearch] = useState('');
  const [knowledgeStatusFilter, setKnowledgeStatusFilter] = useState('all');
  const [knowledgeModuleFilter, setKnowledgeModuleFilter] = useState('all');

  const [supportInsights, setSupportInsights] = useState([]);

  const [billingOverview, setBillingOverview] = useState([]);
  const [billingOverviewLoading, setBillingOverviewLoading] = useState(false);
  const [syncingAsaasPayment, setSyncingAsaasPayment] = useState({});

  const [whatsappOveragePreview, setWhatsappOveragePreview] = useState([]);
  const [whatsappOverageMonth, setWhatsappOverageMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [creatingOverageCharge, setCreatingOverageCharge] = useState({});

  const [leadStats, setLeadStats] = useState({
    total_leads: 0,
    today_leads: 0,
    last_7_days: 0,
    converted_to_trial: 0
  });

  const [supportInsightsLoading, setSupportInsightsLoading] = useState(false);

  const [benefitPrograms, setBenefitPrograms] = useState([]);
  const [benefitProgramsLoading, setBenefitProgramsLoading] = useState(false);
  const [processingBenefit, setProcessingBenefit] = useState({});

  const [securitySummary, setSecuritySummary] = useState(null);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [securityLoading, setSecurityLoading] = useState(false);

  const [systemHealth, setSystemHealth] = useState(null);
  const [systemHealthLoading, setSystemHealthLoading] = useState(false);

  const [securityFilters, setSecurityFilters] = useState({
    dateFrom: '',
    dateTo: '',
    email: '',
    ip: '',
    riskLevel: '',
    eventType: '',
    limit: 100,
  });

  const [showBenefitApprovalModal, setShowBenefitApprovalModal] = useState(false);
  const [selectedBenefitForApproval, setSelectedBenefitForApproval] = useState(null);

  const knowledgeModules = Array.from(
    new Set(
      knowledgeArticles
        .map((article) => article.module)
        .filter(Boolean)
    )
  ).sort();

  const filteredKnowledgeArticles = knowledgeArticles.filter((article) => {
    const text = `${article.title || ''} ${article.slug || ''} ${article.summary || ''} ${
      Array.isArray(article.tags) ? article.tags.join(' ') : ''
    }`.toLowerCase();

    const matchesSearch = !knowledgeSearch.trim()
      || text.includes(knowledgeSearch.trim().toLowerCase());

    const matchesStatus =
      knowledgeStatusFilter === 'all'
      || (knowledgeStatusFilter === 'published' && article.is_published)
      || (knowledgeStatusFilter === 'draft' && !article.is_published);

    const matchesModule =
      knowledgeModuleFilter === 'all'
      || article.module === knowledgeModuleFilter;

    return matchesSearch && matchesStatus && matchesModule;
  });

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8001'
      : '');

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  const tz = process.env.REACT_APP_TIMEZONE || undefined;

  const onlineUsersCount = allUsers.filter((u) => u.is_online).length;

  const formatLastSeen = (iso) => {
    if (!iso) return 'Nunca visto';

    try {
      const dt = new Date(iso);
      const diffMs = Date.now() - dt.getTime();
      const diffMin = Math.max(0, Math.floor(diffMs / 60000));

      if (diffMin < 1) return 'agora';
      if (diffMin === 1) return 'há 1 min';
      if (diffMin < 60) return `há ${diffMin} min`;

      const diffHours = Math.floor(diffMin / 60);
      if (diffHours === 1) return 'há 1 h';
      if (diffHours < 24) return `há ${diffHours} h`;

      return formatDateTime(iso);
    } catch {
      return '-';
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('pt-BR', tz ? { timeZone: tz } : undefined);
    } catch {
      return '-';
    }
  };

  const formatSecurityDateTime = (iso) => {
    if (!iso) return '-';

    try {
      const value = String(iso);

      const normalizedIso =
        value.endsWith('Z') || value.includes('+') || value.includes('-03:00')
          ? value
          : `${value}Z`;

      return new Intl.DateTimeFormat('pt-BR', {
        timeZone: 'America/Recife',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(new Date(normalizedIso));
    } catch {
      return '-';
    }
  };

  const onlyDigits = (value = '') => String(value).replace(/\D/g, '');

  const formatAdminPhone = (value) => {
    const digits = onlyDigits(value);

    if (!digits) return 'N/A';

    if (digits.length === 13 && digits.startsWith('55')) {
      return `(${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }

    if (digits.length === 12 && digits.startsWith('55')) {
      return `(${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
    }

    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return value;
  };

  const openWhatsApp = (phone) => {
    const digits = onlyDigits(phone);

    if (!digits) {
      toast?.error('Telefone não informado.');
      return;
    }

    const number = digits.startsWith('55') ? digits : `55${digits}`;

    window.open(
      `https://wa.me/${number}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const fetchAIStatus = async () => {
    setAiStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/ai-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAiStatus(data);
      } else {
        toast?.error('Erro ao carregar status das IAs');
      }
    } catch (error) {
      console.error('Erro ao carregar status das IAs:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setAiStatusLoading(false);
    }
  };

  const refreshAIStatus = async () => {
    setAiStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/ai-status/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        await response.json();
        toast?.success('Status das IAs atualizado!');
        await fetchAIStatus();
        await fetchAITelemetry();
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Erro ao atualizar status das IAs:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setAiStatusLoading(false);
    }  
  };

  const fetchAITelemetry = async () => {
    setAiTelemetryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/ai-telemetry/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (res.ok) {
        const data = await res.json();
        setAiTelemetry({
          priority: Array.isArray(data?.priority) ? data.priority : [],
          last_used: data?.last_used || null,
          attempts: Array.isArray(data?.attempts) ? data.attempts : []
        });
      } else {
        setAiTelemetry({ priority: [], last_used: null, attempts: [] });
      }
    } catch (e) {
      setAiTelemetry({ priority: [], last_used: null, attempts: [] });
    } finally {
      setAiTelemetryLoading(false);
    }
  };

  const fetchKnowledgeArticles = async () => {
    setKnowledgeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-knowledge`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setKnowledgeArticles(Array.isArray(data?.items) ? data.items : []);
      } else {
        toast?.error('Erro ao carregar base de conhecimento');
      }
    } catch (error) {
      console.error('Erro ao carregar base de conhecimento:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setKnowledgeLoading(false);
    }
  };

  const handleArchiveKnowledgeArticle = async (article) => {
    if (!article?.slug) return;

    const confirmed = window.confirm(
      `Arquivar o artigo "${article.title}"? Ele deixará de ser usado pela IA.`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-knowledge/${article.slug}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ archive: true })
      });

      if (response.ok) {
        toast?.success('Artigo arquivado com sucesso.');
        fetchKnowledgeArticles();
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao arquivar artigo');
      }
    } catch (error) {
      console.error('Erro ao arquivar artigo:', error);
      toast?.error('Erro ao conectar com o servidor');
    }
  };

  const handleOpenKnowledgeHistory = async (article) => {
    if (!article?.slug) return;

    setKnowledgeHistoryLoading(true);
    setShowKnowledgeHistoryModal(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-knowledge/${article.slug}/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setKnowledgeHistory(Array.isArray(data?.items) ? data.items : []);
      } else {
        setKnowledgeHistory([]);
        toast?.error('Erro ao carregar histórico');
      }
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setKnowledgeHistory([]);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setKnowledgeHistoryLoading(false);
    }
  };
  
  const handleSaveKnowledgeArticle = async () => {
    if (!selectedKnowledgeArticle?.title) {
      toast?.error('Informe pelo menos o título do artigo.');
      return;
    }

    setKnowledgeSaving(true);
    try {
      const token = localStorage.getItem('token');

      const payload = {
        slug: selectedKnowledgeArticle.slug,
        title: selectedKnowledgeArticle.title,
        summary: selectedKnowledgeArticle.summary,
        content: selectedKnowledgeArticle.content,
        category: selectedKnowledgeArticle.category,
        module: selectedKnowledgeArticle.module,
        content_type: selectedKnowledgeArticle.content_type,
        video_url: selectedKnowledgeArticle.video_url || null,
        tags: Array.isArray(selectedKnowledgeArticle.tags)
          ? selectedKnowledgeArticle.tags
          : String(selectedKnowledgeArticle.tags || '')
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
        routes: Array.isArray(selectedKnowledgeArticle.routes)
          ? selectedKnowledgeArticle.routes
          : String(selectedKnowledgeArticle.routes || '')
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
        is_published: !!selectedKnowledgeArticle.is_published,
        is_featured: !!selectedKnowledgeArticle.is_featured,
        order_index: Number(selectedKnowledgeArticle.order_index || 9999),
        priority: Number(selectedKnowledgeArticle.priority || 50),
      };

      const isNewArticle = !!selectedKnowledgeArticle.isNew;

      const response = await fetch(
        isNewArticle
          ? `${backendUrl}/api/admin/support-knowledge`
          : `${backendUrl}/api/admin/support-knowledge/${selectedKnowledgeArticle.slug}`,
        {
        method: isNewArticle ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast?.success(selectedKnowledgeArticle.isNew ? 'Artigo criado com sucesso!' : 'Artigo atualizado com sucesso!');
        setShowKnowledgeModal(false);
        setSelectedKnowledgeArticle(null);
        fetchKnowledgeArticles();
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao atualizar artigo');
      }
    } catch (error) {
      console.error('Erro ao salvar artigo:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setKnowledgeSaving(false);
    }
  };

  const handleSuggestKnowledgeArticle = async (item) => {
    if (!item?.question) {
      toast?.error('Pergunta inválida para gerar artigo.');
      return;
    }

    setKnowledgeSuggesting(true);

    try {
      const token = localStorage.getItem('token');

      const firstRoute = Array.isArray(item.routes) && item.routes.length > 0
        ? item.routes[0]
        : '';

      const firstModule = Array.isArray(item.modules) && item.modules.length > 0
        ? item.modules[0]
        : '';

      const response = await fetch(`${backendUrl}/api/admin/support-knowledge/suggest`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question: item.question,
          route: firstRoute,
          module: firstModule,
        })
      });

      if (response.ok) {
        const data = await response.json();
        const article = data?.article || {};

        setSelectedKnowledgeArticle({
          isNew: true,
          slug: article.slug || '',
          title: article.title || '',
          summary: article.summary || '',
          content: article.content || '',
          category: article.category || 'support',
          module: article.module || firstModule || '',
          content_type: article.content_type || 'article',
          video_url: article.video_url || '',
          tags: Array.isArray(article.tags) ? article.tags : [],
          routes: Array.isArray(article.routes) ? article.routes : (firstRoute ? [firstRoute] : []),
          tagsText: Array.isArray(article.tags) ? article.tags.join(', ') : '',
          routesText: Array.isArray(article.routes) ? article.routes.join(', ') : (firstRoute || ''),
          is_published: false,
          is_featured: !!article.is_featured,
          order_index: Number(article.order_index || 9999),
          priority: Number(article.priority || 50),
        });

        setShowKnowledgeModal(true);
        toast?.success('Sugestão de artigo gerada. Revise antes de salvar.');
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao gerar sugestão de artigo');
      }
    } catch (error) {
      console.error('Erro ao gerar sugestão de artigo:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setKnowledgeSuggesting(false);
    }
  };

  const fetchSupportArticlePerformance = async () => {
    setSupportArticlePerformanceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-article-performance?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSupportArticlePerformance(Array.isArray(data?.items) ? data.items : []);
      } else {
        setSupportArticlePerformance([]);
      }
    } catch (error) {
      console.error('Erro ao carregar desempenho dos artigos:', error);
      setSupportArticlePerformance([]);
    } finally {
      setSupportArticlePerformanceLoading(false);
    }
  };

  const fetchSupportFeedback = async () => {
    setSupportFeedbackLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-feedback?limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSupportFeedback({
          summary: data?.summary || {
            total_feedback: 0,
            helpful_count: 0,
            unhelpful_count: 0,
            helpful_rate: 0
          },
          top_routes: Array.isArray(data?.top_routes) ? data.top_routes : [],
          items: Array.isArray(data?.items) ? data.items : []
        });
      } else {
        toast?.error('Erro ao carregar feedback do suporte');
      }
    } catch (error) {
      console.error('Erro ao carregar feedback do suporte:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setSupportFeedbackLoading(false);
    }
  };

  const fetchSupportInsights = async () => {
    setSupportInsightsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/support-feedback/insights`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSupportInsights(Array.isArray(data?.top_problems) ? data.top_problems : []);
      } else {
        setSupportInsights([]);
      }
    } catch (error) {
      console.error('Erro ao carregar insights do suporte:', error);
      setSupportInsights([]);
    } finally {
      setSupportInsightsLoading(false);
    }
  };

  const fetchBillingOverview = async () => {
    setBillingOverviewLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/admin/billing-overview`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBillingOverview(Array.isArray(data?.items) ? data.items : []);
      } else {
        toast?.error('Erro ao carregar visão de cobranças.');
      }
    } catch (error) {
      console.error('Erro ao carregar visão de cobranças:', error);
      toast?.error('Erro ao conectar com o servidor.');
    } finally {
      setBillingOverviewLoading(false);
    }
  };

  const handleSyncAsaasPayment = async (userId) => {
    setSyncingAsaasPayment((prev) => ({
      ...prev,
      [userId]: true,
    }));

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${backendUrl}/api/admin/billing/${userId}/sync-asaas`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast?.error(
          data.detail
          || 'Erro ao sincronizar pagamento com o Asaas.'
        );
        return;
      }

      const statusLabel =
        data.asaas_status || 'atualizado';

      toast?.success(
        `Pagamento sincronizado. Status no Asaas: ${statusLabel}.`
      );

      await Promise.all([
        fetchBillingOverview(),
        fetchAllUsers(),
        fetchAdminStats(),
      ]);

    } catch (error) {
      console.error(
        'Erro ao sincronizar pagamento Asaas:',
        error
      );

      toast?.error(
        'Erro ao conectar com o servidor.'
      );

    } finally {
      setSyncingAsaasPayment((prev) => ({
        ...prev,
        [userId]: false,
      }));
    }
  };

  const fetchWhatsappOveragePreview = async (month = whatsappOverageMonth) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${backendUrl}/api/admin/billing/whatsapp-overage/preview?month=${encodeURIComponent(month)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setWhatsappOveragePreview(Array.isArray(data?.items) ? data.items : []);
      } else {
        toast?.error('Erro ao carregar prévia de excedentes WhatsApp.');
      }
    } catch (error) {
      console.error('Erro ao carregar excedentes WhatsApp:', error);
      toast?.error('Erro ao conectar com o servidor.');
    }
  };

  const handleCreateWhatsappOverageCharge = async (userId, month) => {
    const confirmed = window.confirm(
      'Gerar cobrança avulsa do excedente WhatsApp para este usuário?'
    );

    if (!confirmed) return;

    setCreatingOverageCharge((prev) => ({ ...prev, [userId]: true }));

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/admin/billing/whatsapp-overage/charge`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          month,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast?.error(data.detail || 'Erro ao gerar cobrança de excedente.');
        return;
      }

      toast?.success('Cobrança de excedente gerada com sucesso.');
      await fetchWhatsappOveragePreview(month);
      await fetchBillingOverview();
    } catch (error) {
      console.error('Erro ao gerar cobrança de excedente:', error);
      toast?.error('Erro ao conectar com o servidor.');
    } finally {
      setCreatingOverageCharge((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const fetchLeadStats = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(
        `${backendUrl}/api/admin/marketing-leads`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLeadStats(data);
      }

    } catch (error) {
      console.error('Erro ao carregar leads:', error);
    }
  };  

  const fetchBenefitPrograms = async () => {
    setBenefitProgramsLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/benefits/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBenefitPrograms(Array.isArray(data?.items) ? data.items : []);
      } else {
        toast?.error('Erro ao carregar benefícios acadêmicos.');
      }
    } catch (error) {
      console.error('Erro ao carregar benefícios acadêmicos:', error);
      toast?.error('Erro ao conectar com o servidor.');
    } finally {
      setBenefitProgramsLoading(false);
    }
  };

  const handleApproveBenefit = (benefitId) => {
    const benefit = benefitPrograms.find((item) => item.id === benefitId);

    setSelectedBenefitForApproval(benefit || { id: benefitId });
    setShowBenefitApprovalModal(true);
  };

  const confirmApproveBenefit = async () => {
    const benefitId = selectedBenefitForApproval?.id;
    if (!benefitId) return;

    const formData = new FormData();
    formData.append('validity_days', '90');

    setProcessingBenefit((prev) => ({ ...prev, [benefitId]: true }));

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/benefits/admin/${benefitId}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        toast?.error(data.detail || 'Erro ao aprovar benefício.');
        return;
      }

      toast?.success('Benefício aprovado com sucesso.');
      setShowBenefitApprovalModal(false);
      setSelectedBenefitForApproval(null);

      await fetchBenefitPrograms();
      await fetchAllUsers();
      await fetchAdminStats();
    } catch (error) {
      console.error('Erro ao aprovar benefício:', error);
      toast?.error('Erro ao conectar com o servidor.');
    } finally {
      setProcessingBenefit((prev) => ({ ...prev, [benefitId]: false }));
    }
  };

  const handleRejectBenefit = async (benefitId) => {
    const reason = window.prompt('Informe o motivo da reprovação:', 'Documento inválido ou insuficiente.');
    if (reason === null) return;

    const formData = new FormData();
    formData.append('reason', reason);

    setProcessingBenefit((prev) => ({ ...prev, [benefitId]: true }));

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/benefits/admin/${benefitId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        toast?.error(data.detail || 'Erro ao reprovar benefício.');
        return;
      }

      toast?.success('Benefício reprovado.');
      await fetchBenefitPrograms();
    } catch (error) {
      console.error('Erro ao reprovar benefício:', error);
      toast?.error('Erro ao conectar com o servidor.');
    } finally {
      setProcessingBenefit((prev) => ({ ...prev, [benefitId]: false }));
    }
  };

  const openBenefitProof = async (benefitId) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/benefits/admin/${benefitId}/proof`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        toast?.error('Erro ao abrir comprovante.');
        return;
      }

      const blob = await response.blob();
      const fileUrl = window.URL.createObjectURL(blob);

      window.open(fileUrl, '_blank', 'noopener,noreferrer');

      setTimeout(() => {
        window.URL.revokeObjectURL(fileUrl);
      }, 60000);
    } catch (error) {
      console.error('Erro ao abrir comprovante:', error);
      toast?.error('Erro ao conectar com o servidor.');
    }
  };

  const fetchAdminStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      } else if (response.status === 403) {
        toast?.error('Acesso negado. Privilégios de administrador necessários.');
      } else {
        toast?.error('Erro ao carregar estatísticas administrativas');
      }
    } catch (error) {
      console.error('Erro ao carregar stats:', error);
      toast?.error('Erro ao conectar com o servidor');
    }
  };

  const fetchAllUsers = async () => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data);
        setFilteredUsers(data);
      } else if (response.status === 403) {
        toast?.error('Acesso negado. Privilégios de administrador necessários.');
      } else if (response.status === 404) {
        toast?.error('Endpoint administrativo não encontrado. Verifique a configuração do servidor.');
      } else {
        toast?.error('Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Network error fetching users:', error);
      toast?.error('Erro ao conectar com o servidor');
    } finally {
      setUsersLoading(false);
      setLoading(false);
    }
  };

  const fetchSecurityData = async () => {
    setSecurityLoading(true);

    try {
      const token = localStorage.getItem('token');

      const params = new URLSearchParams();

      params.set('limit', String(securityFilters.limit || 100));

      if (securityFilters.email) params.set('email', securityFilters.email);
      if (securityFilters.ip) params.set('ip', securityFilters.ip);
      if (securityFilters.riskLevel) params.set('risk_level', securityFilters.riskLevel);
      if (securityFilters.eventType) params.set('event_type', securityFilters.eventType);

      if (securityFilters.dateFrom) {
        params.set('date_from', new Date(securityFilters.dateFrom).toISOString());
      }

      if (securityFilters.dateTo) {
        params.set('date_to', new Date(securityFilters.dateTo).toISOString());
      }

      const [summaryRes, logsRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/security/summary`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
        fetch(`${backendUrl}/api/admin/security/logs?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }),
      ]);

      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setSecuritySummary(summary);
      }

      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setSecurityLogs(Array.isArray(logsData?.logs) ? logsData.logs : []);
      }
    } catch (error) {
      console.error('Erro ao carregar segurança:', error);
      toast?.error('Erro ao carregar dados de segurança.');
    } finally {
      setSecurityLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    setSystemHealthLoading(true);

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/admin/system/health`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSystemHealth(data);
      } else {
        toast?.error('Erro ao carregar status da VPS.');
      }
    } catch (error) {
      console.error('Erro ao carregar status da VPS:', error);
      toast?.error('Erro ao conectar com o servidor.');
    } finally {
      setSystemHealthLoading(false);
    }
  };

  const handleCancelWhatsappAsaas = async (userId) => {
    const confirmed = window.confirm(
      'Tentar cancelar a recorrência do módulo WhatsApp no Asaas para este usuário?'
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/admin/billing/whatsapp/${userId}/cancel-asaas`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        toast?.error(data.detail || 'Erro ao cancelar recorrência no Asaas.');
        return;
      }

      toast?.success('Cancelamento enviado ao Asaas.');
      await fetchBillingOverview();
    } catch (error) {
      console.error('Erro ao cancelar recorrência WhatsApp no Asaas:', error);
      toast?.error('Erro ao conectar com o servidor.');
    }
  };

  const handleUpdateSubscriptionStatus = async (userId, newStatus, oldStatus = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription_status: newStatus,
          old_status: oldStatus
        })
      });
      
      if (response.ok) {
        toast?.success(`Status alterado para "${newStatus}" com sucesso!`);
        fetchAllUsers();
        fetchAdminStats();
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao atualizar status');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast?.error('Erro ao conectar com o servidor');
    }
  };

  const handleExtendTrial = async (userId, days = 7) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}/extend-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          days: days,
          reason: `Trial estendido por ${days} dias pelo administrador`
        })
      });
      
      if (response.ok) {
        await response.json();
        toast?.success(`Trial estendido por ${days} dias!`);
        fetchAllUsers();
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao estender trial');
      }
    } catch (error) {
      console.error('Erro ao estender trial:', error);
      toast?.error('Erro ao conectar com o servidor');
    }
  };

  const handleCourtesy = async (
    userId,
    period = '30d',
    enabled = true,
    includeWhatsappModule = false
  ) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}/courtesy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled,
          period,
          include_whatsapp_module: includeWhatsappModule
        })
      });

      if (response.ok) {
        await response.json();
        toast?.success(enabled ? 'Cortesia aplicada com sucesso!' : 'Cortesia removida com sucesso!');
        fetchAllUsers();
        fetchAdminStats();
      } else {
        const error = await response.json();
        toast?.error(error.detail || 'Erro ao atualizar cortesia');
      }
    } catch (error) {
      console.error('Erro ao atualizar cortesia:', error);
      toast?.error('Erro ao conectar com o servidor');
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users/${userId}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedUser(data);
        setShowUserModal(true);
      } else {
        toast?.error('Erro ao carregar detalhes do usuário');
      }
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      toast?.error('Erro ao conectar com o servidor');
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminStats();
      fetchAllUsers();
      fetchAIStatus();
      fetchAITelemetry();
      fetchSupportFeedback();
      fetchSupportArticlePerformance();
      fetchSupportInsights();
      fetchKnowledgeArticles();
      fetchLeadStats();
      fetchBenefitPrograms();
      fetchBillingOverview();
      fetchWhatsappOveragePreview();
      fetchSecurityData();
      fetchSystemHealth();
    }
  }, [user]);

  useEffect(() => {
    let filtered = allUsers;

    if (filterAccountType !== 'all') {
      filtered = filtered.filter((u) => u.account_type === filterAccountType);
    }
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(u => {
        if (filterStatus === 'courtesy') {
          return !!u.courtesy_enabled;
        }
        return (u.effective_subscription_status || u.subscription_status) === filterStatus;
      });
    }
    
    if (searchQuery) {
      filtered = filtered.filter(u => 
        u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.clinic_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.phone?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.billing_phone?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredUsers(filtered);
  }, [allUsers, filterStatus, filterAccountType, searchQuery]);

  const getBillingLocalBadge = (item) => {
    const status = item.local_subscription_status;
    const expiresAt = item.local_subscription_expires;

    const isFutureAccess =
      expiresAt && new Date(expiresAt).getTime() > new Date().getTime();

    if (status === 'canceled' && isFutureAccess) {
      return (
        <div className="space-y-1">
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Cancelamento agendado
          </span>
          <div className="text-xs text-gray-500">
            Acesso até {formatDate(expiresAt)}
          </div>
        </div>
      );
    }

    if (status === 'active' && item.main_subscription_status === 'canceled') {
      return (
        <div className="space-y-1">
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            Cancelamento agendado
          </span>
          <div className="text-xs text-gray-500">
            Acesso até {formatDate(expiresAt)}
          </div>
        </div>
      );
    }

    return getStatusBadge(status);
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      trial: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Trial' },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Ativo' },
      pending_payment: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Aguardando pagamento' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Inativo' },
      suspended: { bg: 'bg-red-100', text: 'text-red-800', label: 'Suspenso' },
      blocked: { bg: 'bg-red-100', text: 'text-red-800', label: 'Bloqueado' },
      canceled: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Cancelamento agendado' },
      courtesy: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Cortesia' },
      academic: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Acadêmico' }
    };
    
    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <Shield className="w-16 h-16 text-red-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Acesso Restrito
          </h3>
          <p className="text-gray-600">
            Esta página é restrita a administradores.
          </p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Painel Administrativo</h1>
            <p className="text-gray-600">Gerencie usuários, assinaturas e feedbacks da plataforma</p>
          </div>
          <button
            onClick={() => {
              fetchAdminStats();
              fetchAllUsers();
              fetchAIStatus();
              fetchAITelemetry();
              fetchSupportFeedback();
              fetchSupportArticlePerformance();
              fetchSupportInsights();
              fetchKnowledgeArticles();
              fetchLeadStats();
              fetchBenefitPrograms();
              fetchBillingOverview();
              fetchWhatsappOveragePreview();
              fetchSecurityData();
              fetchSystemHealth();
            }}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </button>
        </div>

        <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur border border-gray-200 rounded-xl p-3 flex flex-wrap gap-2">
          {[
            ['admin-overview', 'Visão geral'],
            ['admin-commercial', 'Comercial'],
            ['admin-billing', 'Assinaturas e Asaas'],
            ['admin-ai-support', 'Suporte e IA'],
            ['admin-security', 'Segurança e sistema'],
            ['admin-users', 'Clínicas e acessos'],
          ].map(([target, label]) => (
            <button
              key={target}
              type="button"
              onClick={() => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="px-3 py-2 rounded-lg bg-white border border-gray-200 text-sm text-gray-700 hover:border-purple-300 hover:text-purple-700"
            >
              {label}
            </button>
          ))}
        </div>

        <div id="admin-overview" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 scroll-mt-24">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Clínicas</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {adminStats.total_clinics}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <p className="text-sm font-medium text-gray-600">Pessoas com conta</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{adminStats.total_users}</p>
            <p className="text-xs text-gray-500 mt-1">Proprietários e integrantes</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Assinaturas Ativas</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {adminStats.active_subscriptions}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <p className="text-sm font-medium text-gray-600">Integrantes convidados</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">{adminStats.invited_members}</p>
            <p className="text-xs text-gray-500 mt-1">Sem assinatura própria</p>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Em Trial</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {adminStats.trial_users}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Receita Mensal</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {formatCurrency(adminStats.estimated_monthly_revenue)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        <OnboardingAdminPanel />

        <div id="admin-commercial" className="bg-white rounded-xl border border-gray-200 scroll-mt-24">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <GraduationCap className="w-6 h-6 text-indigo-600 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Programa Acadêmico
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Solicitações de alunos de pós-graduação e recém-formados.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={fetchBenefitPrograms}
                disabled={benefitProgramsLoading}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </button>
            </div>
          </div>

          <div className="p-6 overflow-x-auto">
            {benefitProgramsLoading ? (
              <div className="text-sm text-gray-500">Carregando solicitações...</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Usuário
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Instituição / Curso
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Situação
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Envio
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {benefitPrograms.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 align-top">
                        <div className="font-medium text-gray-900">
                          {item.user_name || item.user_email || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.user_email || '-'}
                        </div>
                      </td>

                      <td className="px-3 py-3 align-top">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {item.subtype === 'postgraduate' ? 'Pós-graduação' : 'Recém-formado'}
                        </span>
                      </td>

                      <td className="px-3 py-3 align-top">
                        <div className="font-medium text-gray-900">
                          {item.institution || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.course || '-'}
                        </div>
                        {item.specialty && (
                          <div className="text-xs text-gray-500">
                            Especialidade: {item.specialty}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-3 align-top">
                        {item.status === 'pending' && item.expires_at && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            🔄 Renovação
                          </span>
                        )}

                        {item.status === 'pending' && !item.expires_at && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Nova solicitação
                          </span>
                        )}

                        {item.status === 'approved' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            Benefício ativo
                          </span>
                        )}

                        {item.status === 'expired' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            Expirado
                          </span>
                        )}

                        {item.status === 'rejected' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Reprovado
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 align-top">
                        {item.status === 'pending' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Em análise
                          </span>
                        )}

                        {item.status === 'approved' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Aprovado
                          </span>
                        )}

                        {item.status === 'rejected' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Reprovado
                          </span>
                        )}

                        {item.expires_at && (
                          <div className="text-xs text-gray-500 mt-1">
                            Até {formatDate(item.expires_at)}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-3 align-top text-gray-600">
                        {formatDate(item.created_at)}
                      </td>

                      <td className="px-3 py-3 align-top">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openBenefitProof(item.id)}
                            className="text-xs px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center"
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Ver
                          </button>

                          {item.status === 'pending' && (
                            <>
                              <button
                                type="button"
                                onClick={() => handleApproveBenefit(item.id)}
                                disabled={processingBenefit[item.id]}
                                className="text-xs px-3 py-1.5 rounded border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50"
                              >
                                Aprovar
                              </button>

                              <button
                                type="button"
                                onClick={() => handleRejectBenefit(item.id)}
                                disabled={processingBenefit[item.id]}
                                className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                              >
                                Reprovar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}

                  {benefitPrograms.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-3 py-6 text-center text-gray-500">
                        Nenhuma solicitação acadêmica encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <Mail className="w-6 h-6 text-indigo-600 mr-3" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Leads de Marketing
              </h2>
              <p className="text-gray-600 text-sm">
                Captura de interessados da landing page
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-indigo-50 rounded-lg p-4">
              <p className="text-sm text-indigo-600 font-medium">
                Total
              </p>
              <p className="text-2xl font-bold text-indigo-900 mt-1">
                {leadStats.total_leads}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">
                Hoje
              </p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                {leadStats.today_leads}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">
                Últimos 7 dias
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1">
                {leadStats.last_7_days}
              </p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">
                Viraram Trial
              </p>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                {leadStats.converted_to_trial}
              </p>
            </div>
          </div>
        </div>        

        <div id="admin-billing" className="bg-white rounded-xl border border-gray-200 scroll-mt-24">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Excedentes WhatsApp
                </h2>
                <p className="text-gray-600 text-sm">
                  Prévia mensal de mensagens acima do limite incluso no Módulo WhatsApp Business.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="month"
                  value={whatsappOverageMonth}
                  onChange={(event) => {
                    setWhatsappOverageMonth(event.target.value);
                    fetchWhatsappOveragePreview(event.target.value);
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />

                <button
                  type="button"
                  onClick={() => fetchWhatsappOveragePreview(whatsappOverageMonth)}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm"
                >
                  Atualizar
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Usuário
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Uso
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Excedente
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Valor
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Cobrança
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {whatsappOveragePreview.map((item) => (
                  <tr key={`${item.user_id}-${item.month}`}>
                    <td className="px-3 py-3 align-top">
                      <div className="font-medium text-gray-900">
                        {item.full_name || item.email}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.email}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item.clinic_name || '-'}
                      </div>
                    </td>

                    <td className="px-3 py-3 align-top text-gray-700">
                      {item.messages_sent} / {item.included_limit}
                    </td>

                    <td className="px-3 py-3 align-top">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.overage_messages > 0
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {item.overage_messages} mensagens
                      </span>
                    </td>

                    <td className="px-3 py-3 align-top text-gray-700">
                      R$ {Number(item.overage_amount || 0).toFixed(2).replace('.', ',')}
                    </td>

                    <td className="px-3 py-3 align-top">
                      {item.existing_charge ? (
                        <div className="space-y-1">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {item.existing_charge.gateway_status || item.existing_charge.status}
                          </span>

                          {item.existing_charge.gateway_invoice_url && (
                            <a
                              href={item.existing_charge.gateway_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-xs text-purple-700 hover:underline"
                            >
                              Abrir cobrança
                            </a>
                          )}
                        </div>
                      ) : item.chargeable ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Cobrável
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                          Sem cobrança
                        </span>
                      )}
                    </td>

                    <td className="px-3 py-3 align-top">
                      {item.chargeable && (
                        <button
                          type="button"
                          onClick={() => handleCreateWhatsappOverageCharge(item.user_id, item.month)}
                          disabled={creatingOverageCharge[item.user_id]}
                          className="text-xs px-3 py-1.5 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                        >
                          {creatingOverageCharge[item.user_id] ? 'Gerando...' : 'Gerar cobrança'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}

                {whatsappOveragePreview.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-3 py-6 text-center text-gray-500">
                      Nenhum uso de WhatsApp encontrado para este mês.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Cobranças e Asaas
                </h2>
                <p className="text-gray-600 text-sm">
                  Conciliação entre status local, assinatura principal e módulo WhatsApp no Asaas.
                </p>
              </div>

              <button
                type="button"
                onClick={fetchBillingOverview}
                disabled={billingOverviewLoading}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center disabled:opacity-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar cobranças
              </button>
            </div>
          </div>

          <div className="p-6 overflow-x-auto">
            {billingOverviewLoading ? (
              <div className="text-sm text-gray-500">Carregando cobranças...</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Usuário
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Assinatura local
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Assinatura Asaas
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      WhatsApp local
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      WhatsApp Asaas
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Vencimentos
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Links
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {billingOverview.map((item) => {
                    const hasWhatsappModule = Boolean(
                      item.local_whatsapp_enabled ||
                      item.whatsapp_asaas_status === 'ACTIVE' ||
                      item.whatsapp_gateway_invoice_url
                    );
                    const whatsappLocalLabel = item.local_whatsapp_cancel_at_period_end
                      ? 'Cancelamento solicitado'
                      : item.local_whatsapp_enabled
                        ? 'Ativo'
                        : 'Inativo';

                    const hasMismatch =
                      item.local_whatsapp_cancel_at_period_end &&
                      item.whatsapp_asaas_status === 'ACTIVE';

                    return (
                      <tr
                        key={item.user_id}
                        className={hasMismatch ? 'bg-red-50' : 'hover:bg-gray-50'}
                      >
                        <td className="px-3 py-3 align-top">
                          <div className="font-medium text-gray-900">
                            {item.full_name || item.email}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.email}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.clinic_name || '-'}
                          </div>
                        </td>

                        <td className="px-3 py-3 align-top">
                          {getBillingLocalBadge(item)}
                        </td>

                        <td className="px-3 py-3 align-top">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.main_asaas_status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800'
                                : item.main_asaas_status === 'error'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {item.main_asaas_status || 'Sem recorrência'}
                          </span>

                          {item.main_asaas_error && (
                            <p className="text-xs text-red-600 mt-1 max-w-xs">
                              {item.main_asaas_error}
                            </p>
                          )}
                        </td>

                        <td className="px-3 py-3 align-top">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.local_whatsapp_cancel_at_period_end
                                ? 'bg-yellow-100 text-yellow-800'
                                : item.local_whatsapp_enabled
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {whatsappLocalLabel}
                          </span>
                        </td>

                        <td className="px-3 py-3 align-top">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.whatsapp_asaas_status === 'ACTIVE'
                                ? hasMismatch
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                                : item.whatsapp_asaas_status === 'error'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {item.whatsapp_asaas_status || 'Sem recorrência'}
                          </span>

                          {hasMismatch && (
                            <p className="text-xs text-red-700 mt-1 max-w-xs">
                              Local cancelado, mas recorrência Asaas ainda ativa.
                            </p>
                          )}

                          {hasMismatch && (
                            <button
                              type="button"
                              onClick={() => handleCancelWhatsappAsaas(item.user_id)}
                              className="mt-2 text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-100"
                            >
                              Cancelar no Asaas
                            </button>
                          )}

                          {item.whatsapp_asaas_error && (
                            <p className="text-xs text-red-600 mt-1 max-w-xs">
                              {item.whatsapp_asaas_error}
                            </p>
                          )}
                        </td>

                        <td className="px-3 py-3 align-top text-xs text-gray-600">
                          <div>
                            Assinatura: {formatDate(item.local_subscription_expires)}
                          </div>
                          {hasWhatsappModule && (
                            <div>
                              WhatsApp: {formatDate(item.local_whatsapp_period_end)}
                            </div>
                          )}
                        </td>

                        <td className="px-3 py-3 align-top text-xs">
                          <div className="space-y-1">
                            {item.main_gateway_invoice_url && (
                              <a
                                href={item.main_gateway_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-purple-700 hover:underline"
                              >
                                Cobrança assinatura
                              </a>
                            )}

                            {item.whatsapp_gateway_invoice_url && (
                              <a
                                href={item.whatsapp_gateway_invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-purple-700 hover:underline"
                              >
                                Cobrança WhatsApp
                              </a>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-3 align-top text-xs">
                          <button
                            type="button"
                            onClick={() =>
                              handleSyncAsaasPayment(item.user_id)
                            }
                            disabled={
                              syncingAsaasPayment[item.user_id]
                              || !item.main_subscription_id
                            }
                            title={
                              item.main_subscription_id
                                ? 'Consultar o pagamento vinculado e atualizar os registros locais'
                                : 'Usuário sem assinatura local vinculada'
                            }
                            className="inline-flex items-center px-2.5 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <RefreshCw
                              className={`w-3.5 h-3.5 mr-1.5 ${
                                syncingAsaasPayment[item.user_id]
                                  ? 'animate-spin'
                                  : ''
                              }`}
                            />

                            {syncingAsaasPayment[item.user_id]
                              ? 'Sincronizando...'
                              : 'Sincronizar Asaas'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {billingOverview.length === 0 && (
                    <tr>
                      <td colSpan="8" className="px-3 py-6 text-center text-gray-500">
                        Nenhuma cobrança encontrada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div id="admin-ai-support" className="bg-white rounded-xl border border-gray-200 scroll-mt-24">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Brain className="w-6 h-6 text-purple-600 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Status das IAs</h2>
                  <p className="text-gray-600 text-sm">
                    Monitoramento dos provedores de inteligência artificial
                  </p>
                </div>
              </div>
              <button
                onClick={refreshAIStatus}
                disabled={aiStatusLoading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
              >
                {aiStatusLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Atualizar Status
              </button>
            </div>
          </div>

          {aiStatusLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : aiStatus ? (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Total de Provedores</p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {aiStatus.summary?.total_providers || 0}
                      </p>
                    </div>
                    <Activity className="w-8 h-8 text-blue-600" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Provedores Ativos</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {aiStatus.summary?.active_providers || 0}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">Provedores Inativos</p>
                      <p className="text-2xl font-bold text-red-900 mt-1">
                        {aiStatus.summary?.inactive_providers || 0}
                      </p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-600" />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Provedores de Texto/Chat</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Provedor</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Modelo</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plano/Custo</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Última Checagem</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {aiStatus.text_providers?.map((provider, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">
                            {provider.provider}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center">
                              {provider.configured ? (
                                provider.reachable ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                    <span className="text-green-600">Ativo</span>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-4 h-4 text-red-600 mr-2" />
                                    <span className="text-red-600">Erro</span>
                                  </>
                                )
                              ) : (
                                <>
                                  <Clock className="w-4 h-4 text-yellow-600 mr-2" />
                                  <span className="text-yellow-600">Não Configurado</span>
                                </>
                              )}
                            </div>
                            {provider.error && (
                              <p className="text-xs text-red-500 mt-1">{provider.error}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {provider.model || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              provider.billing_mode === 'free'
                                ? 'bg-green-100 text-green-800'
                                : provider.billing_mode === 'hybrid' || provider.billing_mode === 'trial'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {provider.cost || 'N/A'}
                            </span>
                            {provider.pricing_note && (
                              <p className="text-xs text-gray-500 mt-1">{provider.pricing_note}</p>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {provider.last_check 
                              ? formatDateTime(provider.last_check)
                              : 'Nunca'
                            }
                          </td>
                        </tr>
                      )) || (
                        <tr>
                          <td colSpan="5" className="px-4 py-3 text-center text-gray-500">
                            Nenhum provedor de texto disponível
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-lg border p-4 bg-white">
                  <h3 className="text-md font-semibold text-gray-900 mb-2">Ordem de Prioridade (Failover)</h3>
                  {aiTelemetryLoading ? (
                    <div className="text-sm text-gray-500">Carregando…</div>
                  ) : aiTelemetry.priority?.length > 0 ? (
                    <ol className="list-decimal list-inside space-y-1">
                      {aiTelemetry.priority.map((p, i) => (
                        <li key={i} className="font-medium">{p}</li>
                      ))}
                    </ol>
                  ) : (
                    <div className="text-sm text-gray-500">Não disponível.</div>
                  )}
                </div>

                <div className="rounded-lg border p-4 bg-white">
                  <h3 className="text-md font-semibold text-gray-900 mb-2">Última IA Utilizada</h3>
                  {aiTelemetryLoading ? (
                    <div className="text-sm text-gray-500">Carregando…</div>
                  ) : aiTelemetry.last_used ? (
                    <>
                      <div className="text-sm space-y-1">
                        <div><span className="font-medium">Provedor:</span> {aiTelemetry.last_used.provider || '-'}</div>
                        <div><span className="font-medium">Modelo:</span> {aiTelemetry.last_used.model || '-'}</div>
                        <div><span className="font-medium">Quando:</span> {formatDateTime(aiTelemetry.last_used.at)}</div>
                        {aiTelemetry.last_used.reason && (
                          <div><span className="font-medium">Motivo (fallback):</span> {aiTelemetry.last_used.reason}</div>
                        )}
                        {(aiTelemetry.last_used.duration !== undefined && aiTelemetry.last_used.duration !== null) && (
                          <div><span className="font-medium">Duração:</span> {aiTelemetry.last_used.duration}s</div>
                        )}
                      </div>
                      {Array.isArray(aiTelemetry.attempts) && aiTelemetry.attempts.length > 0 && (
                        <div className="mt-3 border-t pt-2">
                          <div className="font-semibold text-gray-900 mb-1">Tentativas</div>
                          <ol className="list-decimal list-inside space-y-1 text-sm">
                            {aiTelemetry.attempts.map((a, idx) => (
                              <li key={idx}>
                                <span className="font-medium">{a.attempt}ª</span>{' '}
                                – {a.provider}/{a.model} – {a.status === 'success' ? 'ok' : 'falha'}
                                {typeof a.duration === 'number' && <> – {a.duration.toFixed(2)}s</>}
                                {a.reason && <> – motivo: {a.reason}</>}
                                {a.at && <> – {formatDateTime(a.at)}</>}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-sm text-gray-500">Ainda não há registros.</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 text-center">
              <Brain className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Status não disponível</h3>
              <p className="text-gray-600 mb-4">Não foi possível carregar o status das IAs</p>
              <button
                onClick={fetchAIStatus}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
              >
                Tentar Novamente
              </button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <MessageCircleQuestion className="w-6 h-6 text-indigo-600 mr-3" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Feedback do Suporte IA</h2>
                <p className="text-gray-600 text-sm">
                  Acompanhe a qualidade das respostas do chat de suporte
                </p>
              </div>
            </div>
          </div>

          {supportFeedbackLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-indigo-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-indigo-600">Total</p>
                      <p className="text-2xl font-bold text-indigo-900 mt-1">
                        {supportFeedback.summary?.total_feedback || 0}
                      </p>
                    </div>
                    <MessageCircleQuestion className="w-8 h-8 text-indigo-600" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-600">Ajudou</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {supportFeedback.summary?.helpful_count || 0}
                      </p>
                    </div>
                    <ThumbsUp className="w-8 h-8 text-green-600" />
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-600">Não ajudou</p>
                      <p className="text-2xl font-bold text-red-900 mt-1">
                        {supportFeedback.summary?.unhelpful_count || 0}
                      </p>
                    </div>
                    <ThumbsDown className="w-8 h-8 text-red-600" />
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-600">Taxa de ajuda</p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {supportFeedback.summary?.helpful_rate || 0}%
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="rounded-lg border p-4 bg-white">
                <h3 className="text-md font-semibold text-gray-900 mb-3">Rotas com mais feedback</h3>
                {supportFeedback.top_routes?.length > 0 ? (
                  <div className="space-y-2">
                    {supportFeedback.top_routes.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                        <span className="font-medium text-gray-700">{item.route}</span>
                        <span className="text-gray-500">{item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Ainda não há rotas registradas.</div>
                )}
              </div>

              <div className="rounded-lg border bg-white overflow-x-auto">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-md font-semibold text-gray-900">Últimos feedbacks</h3>
                </div>

                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quando</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rota</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pergunta</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Resposta</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avaliação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {supportFeedback.items?.length > 0 ? (
                      supportFeedback.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 align-top">
                          <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                            {formatDateTime(item.created_at)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {item.current_route || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                            <div className="line-clamp-3">{item.question}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-md">
                            <div className="line-clamp-4">{item.answer}</div>
                          </td>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">
                            {item.helpful ? (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                <ThumbsUp className="w-3 h-3" />
                                Ajudou
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                <ThumbsDown className="w-3 h-3" />
                                Não ajudou
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-4 py-6 text-center text-sm text-gray-500">
                          Ainda não há feedback do suporte.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <MessageCircleQuestion className="w-6 h-6 text-emerald-600 mr-3" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Desempenho dos Artigos do Suporte
                </h2>
                <p className="text-gray-600 text-sm">
                  Veja quais artigos estão sendo usados e como foram avaliados
                </p>
              </div>
            </div>
          </div>

          {supportArticlePerformanceLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : supportArticlePerformance.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              Ainda não há dados suficientes de desempenho por artigo.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Artigo
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Usos
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Ajudou
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Não ajudou
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Taxa de ajuda
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {supportArticlePerformance.map((item) => {
                    const needsReview = item.total >= 3 && item.helpful_rate < 70;

                    return (
                      <tr key={item.slug || item.title} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">
                            {item.title || 'Artigo sem título'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.slug || '-'}
                          </div>
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700">
                          {item.total || 0}
                        </td>

                        <td className="px-4 py-3 text-sm text-green-700">
                          {item.helpful_count || 0}
                        </td>

                        <td className="px-4 py-3 text-sm text-red-700">
                          {item.unhelpful_count || 0}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700">
                          {item.helpful_rate || 0}%
                        </td>

                        <td className="px-4 py-3 text-sm">
                          {needsReview ? (
                            <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                              Revisar
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              OK
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center">
              <MessageCircleQuestion className="w-6 h-6 text-red-600 mr-3" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Principais Problemas do Suporte
                </h2>
                <p className="text-gray-600 text-sm">
                  Perguntas marcadas como “Não ajudou”, agrupadas por recorrência
                </p>
              </div>
            </div>
          </div>

          {supportInsightsLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            </div>
          ) : supportInsights.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              Ainda não há problemas recorrentes identificados.
            </div>
          ) : (
            <div className="p-6 space-y-3">
              {supportInsights.map((item, index) => (
                <div key={index} className="rounded-lg border border-red-100 bg-red-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-gray-900">
                        {item.question}
                      </p>
                      <p className="text-xs text-gray-600 mt-2">
                        Rotas: {Array.isArray(item.routes) && item.routes.length > 0
                          ? item.routes.join(', ')
                          : 'N/A'}
                      </p>
                    </div>

                    <div className="shrink-0 flex flex-col items-end gap-2">
                      <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                        {item.count}x
                      </span>

                      <button
                        type="button"
                        onClick={() => handleSuggestKnowledgeArticle(item)}
                        disabled={knowledgeSuggesting}
                        className="px-3 py-1 rounded-lg bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
                      >
                        {knowledgeSuggesting ? 'Gerando...' : 'Gerar artigo'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center">
                <MessageCircleQuestion className="w-6 h-6 text-purple-600 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Base de Conhecimento do Suporte
                  </h2>
                  <p className="text-gray-600 text-sm">
                    Edite, filtre e publique os artigos usados pelo chat de suporte IA
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedKnowledgeArticle({
                    isNew: true,
                    slug: '',
                    title: '',
                    summary: '',
                    content: '',
                    category: 'support',
                    module: '',
                    content_type: 'article',
                    video_url: '',
                    tags: [],
                    routes: [],
                    tagsText: '',
                    routesText: '',
                    is_published: true,
                    is_featured: false,
                    order_index: 9999,
                    priority: 50,
                  });
                  setShowKnowledgeModal(true);
                }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm font-medium"
              >
                + Novo artigo
              </button>
            </div>
          </div>

          <div className="p-6 border-b border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Buscar por título, slug, resumo ou tag..."
              value={knowledgeSearch}
              onChange={(e) => setKnowledgeSearch(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />

            <select
              value={knowledgeStatusFilter}
              onChange={(e) => setKnowledgeStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              <option value="published">Publicados</option>
              <option value="draft">Rascunhos</option>
            </select>

            <select
              value={knowledgeModuleFilter}
              onChange={(e) => setKnowledgeModuleFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">Todos os módulos</option>
              {knowledgeModules.map((moduleName) => (
                <option key={moduleName} value={moduleName}>
                  {moduleName}
                </option>
              ))}
            </select>
          </div>

          {knowledgeLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ordem</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Módulo</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Publicado</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredKnowledgeArticles.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-6 text-center text-sm text-gray-500">
                        Nenhum artigo encontrado.
                      </td>
                    </tr>
                  ) : (
                    filteredKnowledgeArticles.map((article) => (
                      <tr key={article.slug} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {article.order_index}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{article.title}</div>
                          <div className="text-xs text-gray-500">{article.slug}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {article.module || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                          {Array.isArray(article.tags) ? article.tags.join(', ') : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {article.is_published ? (
                            <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                              Sim
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                              Não
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => {
                                setSelectedKnowledgeArticle({
                                  ...article,
                                  tagsText: Array.isArray(article.tags) ? article.tags.join(', ') : '',
                                  routesText: Array.isArray(article.routes) ? article.routes.join(', ') : '',
                                });
                                setShowKnowledgeModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                            >
                              Editar
                            </button>

                            <button
                              onClick={() => handleOpenKnowledgeHistory(article)}
                              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                            >
                              Histórico
                            </button>

                            <button
                              onClick={() => handleArchiveKnowledgeArticle(article)}
                              className="text-red-600 hover:text-red-900 text-sm font-medium"
                            >
                              Arquivar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-green-600" />
                  Infraestrutura VPS
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Monitoramento básico do servidor, disco, backups, uploads e serviços.
                </p>
              </div>

              <button
                type="button"
                onClick={fetchSystemHealth}
                disabled={systemHealthLoading}
                className="text-sm bg-green-50 text-green-700 px-3 py-2 rounded-lg hover:bg-green-100 disabled:opacity-50"
              >
                Atualizar VPS
              </button>
            </div>
          </div>

          <div className="p-6">
            {systemHealthLoading ? (
              <div className="text-sm text-gray-500">Carregando status da VPS...</div>
            ) : !systemHealth ? (
              <div className="text-sm text-gray-500">Status ainda não carregado.</div>
            ) : (
              <>
                {systemHealth.alerts?.length > 0 && (
                  <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                    <div className="font-medium text-yellow-800 mb-1">Alertas</div>
                    {systemHealth.alerts.map((alert, index) => (
                      <div key={index} className="text-sm text-yellow-700">
                        {alert}
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  {['root', 'home'].map((key) => {
                    const disk = systemHealth.disk?.[key];

                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600">Disco {disk?.path}</p>
                        <p className={`text-2xl font-bold ${
                          disk?.status === 'critical'
                            ? 'text-red-700'
                            : disk?.status === 'warning'
                            ? 'text-yellow-700'
                            : 'text-green-700'
                        }`}>
                          {disk?.percent ?? '-'}%
                        </p>
                        <p className="text-xs text-gray-500">
                          Livre: {disk?.free_gb ?? '-'} GB / Total: {disk?.total_gb ?? '-'} GB
                        </p>
                      </div>
                    );
                  })}

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Uptime</p>
                    <p className="text-lg font-bold text-gray-900">
                      {systemHealth.server?.uptime || '-'}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Status geral</p>
                    <p className={`text-lg font-bold ${
                      systemHealth.status === 'critical'
                        ? 'text-red-700'
                        : systemHealth.status === 'warning'
                        ? 'text-yellow-700'
                        : 'text-green-700'
                    }`}>
                      {systemHealth.status === 'critical'
                        ? 'Crítico'
                        : systemHealth.status === 'warning'
                        ? 'Atenção'
                        : 'OK'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Pastas críticas</h3>
                    <div className="space-y-2">
                      {systemHealth.folders?.map((folder) => (
                        <div key={folder.path} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm">
                          <span className="text-gray-700 truncate mr-3">{folder.path}</span>
                          <span className={`font-medium ${folder.status === 'warning' ? 'text-yellow-700' : 'text-gray-900'}`}>
                            {folder.exists ? `${folder.size_gb} GB` : 'não existe'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Serviços</h3>
                    <div className="space-y-2">
                      {systemHealth.services?.map((service) => (
                        <div key={service.service} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm">
                          <span className="text-gray-700">{service.service}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            service.status === 'ok'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {service.active}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <h3 className="font-medium text-gray-900 mb-2">Memória</h3>
                  <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto">
                    {systemHealth.memory_raw || '-'}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>

        <div id="admin-security" className="bg-white rounded-xl border border-gray-200 scroll-mt-24">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-purple-600" />
                  Segurança
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Eventos relevantes de segurança, ações administrativas, bloqueios, erros e tentativas suspeitas.
                </p>
              </div>

              <button
                onClick={fetchSecurityData}
                className="text-sm bg-purple-50 text-purple-700 px-3 py-2 rounded-lg hover:bg-purple-100"
              >
                Atualizar segurança
              </button>
            </div>
          </div>

          <div className="p-6">
            {securityLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">Eventos 24h</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {securitySummary?.total_24h || 0}
                    </p>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-sm text-yellow-700">Risco médio</p>
                    <p className="text-2xl font-bold text-yellow-800">
                      {securitySummary?.medium_24h || 0}
                    </p>
                  </div>

                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-sm text-red-700">Risco alto</p>
                    <p className="text-2xl font-bold text-red-800">
                      {securitySummary?.high_24h || 0}
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-blue-700">IPs principais</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {securitySummary?.top_ips?.length || 0}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-6">
                  <input
                    type="datetime-local"
                    value={securityFilters.dateFrom}
                    onChange={(e) => setSecurityFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="De"
                  />

                  <input
                    type="datetime-local"
                    value={securityFilters.dateTo}
                    onChange={(e) => setSecurityFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="Até"
                  />

                  <input
                    type="text"
                    value={securityFilters.email}
                    onChange={(e) => setSecurityFilters((prev) => ({ ...prev, email: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="E-mail"
                  />

                  <input
                    type="text"
                    value={securityFilters.ip}
                    onChange={(e) => setSecurityFilters((prev) => ({ ...prev, ip: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="IP"
                  />

                  <select
                    value={securityFilters.riskLevel}
                    onChange={(e) => setSecurityFilters((prev) => ({ ...prev, riskLevel: e.target.value }))}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">Todos riscos</option>
                    <option value="low">Baixo</option>
                    <option value="medium">Médio</option>
                    <option value="high">Alto</option>
                  </select>

                  <button
                    type="button"
                    onClick={fetchSecurityData}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm"
                  >
                    Filtrar
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Horário
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Evento
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Risco
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          IP
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Usuário
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Método
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Endpoint
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Status
                        </th>
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {securityLogs.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-4 py-6 text-center text-gray-500">
                            Nenhum evento de segurança encontrado.
                          </td>
                        </tr>
                      ) : (
                        securityLogs.map((item) => (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {formatSecurityDateTime(item.created_at)}
                            </td>

                            <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                              {item.event_type}
                            </td>

                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.risk_level === 'high'
                                  ? 'bg-red-100 text-red-800'
                                  : item.risk_level === 'medium'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {item.risk_level || 'low'}
                              </span>
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {item.ip || '-'}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              <div className="font-medium text-gray-900">
                                {item.details?.full_name || item.email || '-'}
                              </div>
                              {item.email && (
                                <div className="text-xs text-gray-500">
                                  {item.email}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {item.method || '-'}
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-700">
                              <div className="max-w-xs truncate" title={item.path}>
                                {item.path || '-'}
                              </div>
                            </td>

                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {item.status_code || '-'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        <div id="admin-users" className="bg-white rounded-xl border border-gray-200 scroll-mt-24">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Clínicas e pessoas com acesso
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {filteredUsers.length} de {allUsers.length} pessoas • {adminStats.total_clinics} clínicas • {onlineUsersCount} online
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mt-4 sm:mt-0">
                <input
                  type="text"
                  placeholder="Buscar usuários..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />

                <select
                  value={filterAccountType}
                  onChange={(e) => setFilterAccountType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Proprietários e integrantes</option>
                  <option value="owner">Somente proprietários</option>
                  <option value="member">Somente convidados</option>
                </select>
                
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Todos os Status</option>
                  <option value="trial">Em Trial</option>
                  <option value="active">Assinatura ativa</option>
                  <option value="suspended">Suspensos</option>
                  <option value="courtesy">Cortesia</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {usersLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clínica
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Online
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data de Cadastro
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expira em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                        {allUsers.length === 0 ? 'Nenhum usuário encontrado' : 'Nenhum usuário corresponde aos filtros'}
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email}
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                user.is_subscription_owner
                                  ? 'bg-purple-100 text-purple-800'
                                  : 'bg-sky-100 text-sky-800'
                              }`}>
                                {user.is_subscription_owner ? 'Proprietário' : 'Convidado'}
                              </span>
                              <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                                {user.clinic_role_label || 'Sem função'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.clinic_name || 'N/A'}
                          </div>

                          {!user.is_subscription_owner && user.subscription_owner_name && (
                            <div className="text-xs text-gray-500 mt-1">
                              Responsável: {user.subscription_owner_name}
                            </div>
                          )}

                          {user.initial_clinic_name &&
                            user.initial_clinic_name !== user.clinic_name && (
                              <div className="text-xs text-amber-700 mt-1">
                                Cadastro inicial: {user.initial_clinic_name}
                              </div>
                            )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatAdminPhone(user.phone)}
                          </div>

                          {user.billing_phone && user.billing_phone !== user.phone && (
                            <div className="text-xs text-gray-500">
                              Cobrança: {formatAdminPhone(user.billing_phone)}
                            </div>
                          )}

                          {(user.phone || user.billing_phone) && (
                            <button
                              type="button"
                              onClick={() => openWhatsApp(user.phone || user.billing_phone)}
                              className="text-xs text-green-700 hover:text-green-900 mt-1"
                            >
                              Abrir WhatsApp
                            </button>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.is_online ? (
                            <div className="space-y-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Online
                              </span>
                              {user.current_route && (
                                <div className="text-xs text-gray-500">
                                  {user.current_route}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                Offline
                              </span>
                              <div className="text-xs text-gray-500">
                                {formatLastSeen(user.last_seen_at)}
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                user.is_active !== false
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                Conta {user.is_active !== false ? 'habilitada' : 'desabilitada'}
                              </span>
                            </div>
                            <div>
                              {getStatusBadge(
                                user.is_subscription_owner && user.courtesy_enabled
                                  ? 'courtesy'
                                  : (user.effective_subscription_status || user.subscription_status)
                              )}
                            </div>
                            {!user.is_subscription_owner && (
                              <div className="text-xs text-gray-500">Assinatura herdada da clínica</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.is_subscription_owner && user.courtesy_enabled
                            ? (user.courtesy_until ? `Cortesia até ${formatDate(user.courtesy_until)}` : 'Cortesia indefinida')
                            : formatDate(user.effective_subscription_expires || user.subscription_expires)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => viewUserDetails(user.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Ver detalhes"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            
                            {user.is_subscription_owner && user.subscription_status === 'trial' && (
                              <button
                                onClick={() => handleExtendTrial(user.id, 7)}
                                className="text-green-600 hover:text-green-900"
                                title="Estender trial"
                              >
                                <Calendar className="w-4 h-4" />
                              </button>
                            )}

                            {user.is_subscription_owner && <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  setCourtesyUserId(user.id);
                                  setCourtesyPeriod(e.target.value);
                                  setIncludeWhatsappCourtesy(false);
                                  setShowCourtesyModal(true);
                                  e.target.value = '';
                                }
                              }}
                              className="text-xs border border-purple-300 rounded px-2 py-1 hover:bg-purple-50"
                              defaultValue=""
                            >
                              <option value="" disabled>Cortesia</option>
                              <option value="30d">30 dias</option>
                              <option value="2m">2 meses</option>
                              <option value="3m">3 meses</option>
                              <option value="6m">6 meses</option>
                              <option value="1y">1 ano</option>
                              <option value="indefinite">Indefinido</option>
                            </select>}

                            {user.is_subscription_owner && user.courtesy_enabled && (
                              <button
                                onClick={() => handleCourtesy(user.id, '30d', false)}
                                className="text-purple-600 hover:text-purple-900"
                                title="Remover cortesia"
                              >
                                <Zap className="w-4 h-4" />
                              </button>
                            )}
                            
                            {user.is_subscription_owner && <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  handleUpdateSubscriptionStatus(user.id, e.target.value, user.subscription_status);
                                  e.target.value = '';
                                }
                              }}
                              className="text-xs border border-gray-300 rounded px-2 py-1 hover:bg-gray-50"
                              defaultValue=""
                            >
                              <option value="" disabled>Alterar Status</option>
                              {user.subscription_status !== 'trial' && (
                                <option value="trial">→ Trial</option>
                              )}
                              {user.subscription_status !== 'active' && (
                                <option value="active">→ Ativo</option>
                              )}
                              {user.subscription_status !== 'suspended' && (
                                <option value="suspended">→ Suspender</option>
                              )}
                            </select>}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {showKnowledgeHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Histórico do Artigo
                </h3>
                <button
                  onClick={() => {
                    setShowKnowledgeHistoryModal(false);
                    setKnowledgeHistory([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="p-6">
                {knowledgeHistoryLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : knowledgeHistory.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    Ainda não há histórico para este artigo.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {knowledgeHistory.map((item) => (
                      <div key={item.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium text-gray-900">
                            {item.action}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(item.created_at)}
                          </div>
                        </div>

                        <div className="text-xs text-gray-600 mb-2">
                          Admin: {item.admin_user_name || '-'}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Antes</div>
                            <pre className="bg-white border rounded p-2 overflow-x-auto max-h-64 whitespace-pre-wrap">
                              {JSON.stringify(item.before || {}, null, 2)}
                            </pre>
                          </div>

                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Depois</div>
                            <pre className="bg-white border rounded p-2 overflow-x-auto max-h-64 whitespace-pre-wrap">
                              {JSON.stringify(item.after || {}, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showKnowledgeModal && selectedKnowledgeArticle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedKnowledgeArticle.isNew ? 'Novo Artigo de Suporte' : 'Editar Artigo de Suporte'}
                </h3>
                <button
                  onClick={() => {
                    setShowKnowledgeModal(false);
                    setSelectedKnowledgeArticle(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug
                    </label>
                    <input
                      value={selectedKnowledgeArticle.slug || ''}
                      disabled={!selectedKnowledgeArticle.isNew}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          slug: e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9\s-]/g, '')
                            .replace(/\s+/g, '-')
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 disabled:bg-gray-100"
                      placeholder="ex: imagens-stl-3d"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título
                    </label>
                    <input
                      value={selectedKnowledgeArticle.title || ''}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          title: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Módulo
                    </label>
                    <input
                      value={selectedKnowledgeArticle.module || ''}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          module: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Resumo
                  </label>
                  <input
                    value={selectedKnowledgeArticle.summary || ''}
                    onChange={(e) =>
                      setSelectedKnowledgeArticle({
                        ...selectedKnowledgeArticle,
                        summary: e.target.value
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Conteúdo
                  </label>
                  <textarea
                    rows={8}
                    value={selectedKnowledgeArticle.content || ''}
                    onChange={(e) =>
                      setSelectedKnowledgeArticle({
                        ...selectedKnowledgeArticle,
                        content: e.target.value
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags, separadas por vírgula
                    </label>
                    <textarea
                      rows={3}
                      value={selectedKnowledgeArticle.tagsText || ''}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          tagsText: e.target.value,
                          tags: e.target.value
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean)
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rotas, separadas por vírgula
                    </label>
                    <textarea
                      rows={3}
                      value={selectedKnowledgeArticle.routesText || ''}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          routesText: e.target.value,
                          routes: e.target.value
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean)
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria
                    </label>
                    <input
                      value={selectedKnowledgeArticle.category || ''}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          category: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo
                    </label>
                    <input
                      value={selectedKnowledgeArticle.content_type || 'article'}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          content_type: e.target.value
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ordem
                    </label>
                    <input
                      type="number"
                      value={selectedKnowledgeArticle.order_index || 9999}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          order_index: Number(e.target.value)
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prioridade
                    </label>
                    <input
                      type="number"
                      value={selectedKnowledgeArticle.priority || 50}
                      onChange={(e) =>
                        setSelectedKnowledgeArticle({
                          ...selectedKnowledgeArticle,
                          priority: Number(e.target.value)
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="50 padrão, 100 alta, 10 baixa"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Quanto maior, maior a chance deste artigo aparecer primeiro no suporte IA.
                    </p>
                  </div>

                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!selectedKnowledgeArticle.is_published}
                        onChange={(e) =>
                          setSelectedKnowledgeArticle({
                            ...selectedKnowledgeArticle,
                            is_published: e.target.checked
                          })
                        }
                      />
                      Publicado
                    </label>

                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!selectedKnowledgeArticle.is_featured}
                        onChange={(e) =>
                          setSelectedKnowledgeArticle({
                            ...selectedKnowledgeArticle,
                            is_featured: e.target.checked
                          })
                        }
                      />
                      Destaque
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL do vídeo
                  </label>
                  <input
                    value={selectedKnowledgeArticle.video_url || ''}
                    onChange={(e) =>
                      setSelectedKnowledgeArticle({
                        ...selectedKnowledgeArticle,
                        video_url: e.target.value
                      })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowKnowledgeModal(false);
                      setSelectedKnowledgeArticle(null);
                    }}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>

                  <button
                    onClick={handleSaveKnowledgeArticle}
                    disabled={knowledgeSaving}
                    className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
                  >
                    {knowledgeSaving ? 'Salvando...' : 'Salvar artigo'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCourtesyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">
                  Conceder Cortesia
                </h3>
              </div>

              <div className="p-6 space-y-4">

                <p className="text-sm text-gray-600">
                  Deseja incluir também o Módulo WhatsApp Business nesta cortesia?
                </p>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeWhatsappCourtesy}
                    onChange={(e) =>
                      setIncludeWhatsappCourtesy(e.target.checked)
                    }
                  />
                  <span>
                    Incluir Módulo WhatsApp Business
                  </span>
                </label>

              </div>

              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCourtesyModal(false);
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancelar
                </button>

                <button
                  onClick={() => {
                    handleCourtesy(
                      courtesyUserId,
                      courtesyPeriod,
                      true,
                      includeWhatsappCourtesy
                    );

                    setShowCourtesyModal(false);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg"
                >
                  Aplicar cortesia
                </button>
              </div>
            </div>
          </div>
        )}

        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  Detalhes do Usuário
                </h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">
                    Informações Pessoais
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Nome</p>
                      <p className="font-medium">{selectedUser.full_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{selectedUser.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tipo de acesso</p>
                      <p className="font-medium">
                        {selectedUser.is_subscription_owner ? 'Proprietário da assinatura' : 'Integrante convidado'}
                      </p>
                      <p className="text-xs text-gray-500">{selectedUser.clinic_role_label}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Assinatura da clínica</p>
                      {getStatusBadge(
                        selectedUser.is_subscription_owner && selectedUser.courtesy_enabled
                          ? 'courtesy'
                          : (selectedUser.effective_subscription_status || selectedUser.subscription_status)
                      )}
                      {!selectedUser.is_subscription_owner && selectedUser.subscription_owner_name && (
                        <p className="text-xs text-gray-500 mt-1">
                          Responsável: {selectedUser.subscription_owner_name}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Data de Cadastro</p>
                      <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                    </div>
                  </div>
                </div>

                {selectedUser.clinic_settings && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">
                      Configurações da Clínica
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Nome da Clínica</p>
                        <p className="font-medium">{selectedUser.clinic_settings.clinic_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">CNPJ</p>
                        <p className="font-medium">{selectedUser.clinic_settings.cnpj || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedUser.is_subscription_owner ? <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">
                    Estatísticas de Uso
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedUser.statistics?.total_patients || 0}
                      </p>
                      <p className="text-sm text-gray-600">Pacientes</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">
                        {selectedUser.statistics?.total_images || 0}
                      </p>
                      <p className="text-sm text-gray-600">Imagens</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">
                        {selectedUser.statistics?.total_treatments || 0}
                      </p>
                      <p className="text-sm text-gray-600">Tratamentos</p>
                    </div>
                  </div>
                </div> : (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
                    As estatísticas clínicas pertencem à clínica compartilhada. Este perfil mostra apenas a identidade e o vínculo da pessoa convidada.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {showBenefitApprovalModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Aprovar benefício
              </h3>

              <p className="text-sm text-gray-600 mb-4">
                Este benefício será aprovado por 90 dias, conforme a regra atual do Programa de Benefícios.
              </p>

              {selectedBenefitForApproval && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-5 text-sm text-gray-700">
                  <p>
                    <strong>Usuário:</strong>{' '}
                    {selectedBenefitForApproval.user_name || selectedBenefitForApproval.user_email || '-'}
                  </p>
                  <p>
                    <strong>Benefício:</strong>{' '}
                    {selectedBenefitForApproval.benefit_type_name ||
                      (selectedBenefitForApproval.subtype === 'postgraduate'
                        ? 'Programa Acadêmico - Pós-graduação'
                        : 'Programa Recém-formado')}
                  </p>
                  <p>
                    <strong>Instituição:</strong>{' '}
                    {selectedBenefitForApproval.institution || '-'}
                  </p>
                </div>
              )}

              <div className="rounded-xl border border-green-200 bg-green-50 p-4 mb-5">
                <p className="text-sm text-green-900">
                  <strong>Validade:</strong> 90 dias
                </p>
                <p className="text-xs text-green-800 mt-1">
                  O benefício é concedido uma única vez por usuário e não possui renovação automática.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowBenefitApprovalModal(false);
                    setSelectedBenefitForApproval(null);
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={confirmApproveBenefit}
                  disabled={processingBenefit[selectedBenefitForApproval?.id]}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {processingBenefit[selectedBenefitForApproval?.id]
                    ? 'Aprovando...'
                    : 'Aprovar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPage;
